import { displayText, roleLabel, csvHeading } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeContext from '../../../ThemeContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../../services/api';
import ReportPieChart from './ReportPieChart';

const UsageReports = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Role & base path detection
  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const role = (userInfo.role || 'reseller').toLowerCase();

  const navItems = {
    superadmin: ['resellers', 'clients', 'reports', 'notify', 'admins', 'account'],
    admin: ['resellers', 'clients', 'reports', 'account'],
    reseller: ['clients', 'reports', 'account']
  };

  const tabs = navItems[role] || navItems.reseller;

  const basePath = role === 'superadmin'
    ? '/superadmin'
    : role === 'admin'
      ? '/admin'
      : `/${userInfo.username || 'reseller'}`;

  // Typography
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;

  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = useState(false);

  const clientsDropdownRef = useRef(null);
  const reportsDropdownRef = useRef(null);

const searchParams = new URLSearchParams(location.search);
const urlView = searchParams.get("view");

const [viewMode, setViewMode] = useState(
  urlView === "pie-chart" ? "pie-chart" : "report"
);

  // Filters & date range
  const incomingFilters = location.state?.filters || {};
  const selectedResellerIds = (location.state?.selectedResellerIds || []).map(String);

  const today = new Date();
  const currentYear = today.getFullYear();
  const pad = (n) => String(n).padStart(2, '0');

  const defaultStart = `${currentYear}-01-01`;
  const defaultEnd = `${currentYear}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const initialStart = incomingFilters.startDate || defaultStart;
  const initialEnd = incomingFilters.endDate || defaultEnd;

  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [appliedStartDate, setAppliedStartDate] = useState(initialStart);
  const [appliedEndDate, setAppliedEndDate] = useState(initialEnd);

  // Usage data
  const [usageCards, setUsageCards] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const lastFetchKeyRef = useRef(null);

  const isActive = (tab) => location.pathname.includes(`/${tab}`);

  const handleTabClick = (tab) => {
    if (tab === 'clients') {
      navigate(`${basePath}/clients`);
    } else if (tab === 'reports') {
      navigate(`${basePath}/reports?sub=usage`);
    } else {
      navigate(`${basePath}/${tab}`);
    }
  };

  const handleClientSubTab = (subTab) => {
    const path = subTab === 'enterprise' ? 'enterprise-clients' : 'clients';
    navigate(`${basePath}/${path}`);
    setIsClientsDropdownOpen(false);
  };

  const handleReportSubTab = (subTabKey) => {
    navigate(`${basePath}/reports?sub=${subTabKey}`);
    setIsReportsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target)) {
        setIsClientsDropdownOpen(false);
      }
      if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(e.target)) {
        setIsReportsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const parseDateSafely = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = parseDateSafely(value);
    if (!d) return String(value);
    return d.toLocaleDateString();
  };

  const extractRoleFromIntroducingParty = (introducingParty) => {
    if (!introducingParty) return '';
    const m = String(introducingParty).match(/\(([^)]+)\)/);
    return m && m[1] ? m[1].trim() : '';
  };
useEffect(() => {
  if (urlView && (urlView === "report" || urlView === "pie-chart")) {
    setViewMode(urlView);
  }
}, [urlView]);


  useEffect(() => {
    const fetchUsageData = async () => {
      const fetchKey = `${appliedStartDate}|${appliedEndDate}|${selectedResellerIds.join(',')}`;
      if (lastFetchKeyRef.current === fetchKey) return;
      lastFetchKeyRef.current = fetchKey;

      setLoadingUsage(true);
      try {
        const start = parseDateSafely(appliedStartDate);
        const end = parseDateSafely(appliedEndDate);

        if (!start || !end || end.getTime() < start.getTime()) {
          toast.error('Invalid date range.');
          setUsageCards([]);
          return;
        }

        const [resellersResp, clientAdminsResp, casesResp, usersResp] = await Promise.all([
          api.get('/resellers'),
          api.get('/client-admins'),
          api.get('/cases'),
          api.get('/users'),
        ]);

        const resellers = resellersResp.data?.data || resellersResp.data || [];
        const rawClientAdmins = clientAdminsResp.data?.data || clientAdminsResp.data || [];
        const cases = casesResp.data?.data || casesResp.data?.data || casesResp.data || [];
        const users = usersResp.data?.users || [];

        const resellerById = {};
        resellers.forEach(r => {
          const id = r.id ?? r.reseller_id ?? r._id;
          if (id != null) resellerById[String(id)] = r;
        });

        // Filter client admins
        let allowedClientAdmins = rawClientAdmins.filter(ca => {
          const creatorRole = (ca.created_by_role || ca.creator_role || '').toLowerCase();
          const resellerId = ca.reseller_id ?? ca.resellerId ?? ca.reseller?.id;

          if (role === 'reseller') {
            return String(resellerId) === String(userInfo.id || userInfo.reseller_id || userInfo._id);
          }
          // superadmin/admin see reseller-created clients
          return creatorRole === 'reseller' || !creatorRole;
        });

        if (selectedResellerIds.length > 0 && role !== 'reseller') {
          allowedClientAdmins = allowedClientAdmins.filter(ca => {
            const rid = ca.reseller_id ?? ca.resellerId ?? ca.reseller?.id;
            return rid && selectedResellerIds.includes(String(rid));
          });
        }

        const usersById = {};
        users.forEach(u => {
          if (u.id != null) usersById[String(u.id)] = u;
        });

        const clientAdminByEmail = {};
        const clientAdminById = {};
        allowedClientAdmins.forEach(ca => {
          if (ca.id != null) clientAdminById[String(ca.id)] = ca;
          const emails = [
            (ca.primary_email || '').toLowerCase(),
            (ca.email || '').toLowerCase(),
            (ca.user?.email || '').toLowerCase(),
          ].filter(Boolean);
          emails.forEach(e => clientAdminByEmail[e] = ca);
        });

        const isInRange = (d) => d && d.getTime() >= start.getTime() && d.getTime() <= end.getTime();

        const cardsByClientAdminId = {};
        allowedClientAdmins.forEach((ca) => {
          const key = String(ca.id);
          const resellerId = ca.reseller_id ?? ca.resellerId ?? ca.reseller?.id;
          const reseller = resellerId ? resellerById[String(resellerId)] || ca.reseller || {} : {};
          const location = ca.location || ca.city || reseller.city || 'Unknown';
          cardsByClientAdminId[key] = { key, clientAdmin: ca, reseller, location, rows: [] };
        });

        await Promise.all(
          cases.map(async (cs) => {
            const caseId = cs.id;
            if (!caseId) return;

            const clientUser = cs.client || cs.client_user || usersById[String(cs.client_id ?? cs.clientId ?? '')] || null;
            const clientEmail = (clientUser?.email || cs.email || '').toLowerCase();

            const clientAdmin =
              (clientEmail && clientAdminByEmail[clientEmail]) ||
              (cs.client_admin_id && clientAdminById[String(cs.client_admin_id)]);

            if (!clientAdmin) return;

            const cardKey = String(clientAdmin.id);
            let card = cardsByClientAdminId[cardKey];
            if (!card) return;

            const sessionTypes = ['deposition-sessions', 'arbitrations', 'trials', 'mediations', 'witness-preps'];
            const sessionLabels = ['Deposition', 'Arbitration', 'Trial', 'Mediation', 'WitnessPrep'];

            const responses = await Promise.all(
              sessionTypes.map(type => api.get(`/cases/${caseId}/${type}`).catch(() => ({ data: [] })))
            );

            const allSessions = responses.flatMap((resp, idx) =>
              (resp.data || []).map(s => ({ ...s, _sessionType: sessionLabels[idx] }))
            );

            if (!allSessions.length) return;

            const caseName = cs.case_name || cs.name || `${displayText('Case')} #${cs.id || ''}` || displayText('Unknown Case');

            allSessions.forEach(session => {
              const sessionDate = parseDateSafely(session.date || session.session_date || session.created_at || session.createdAt);
              if (!sessionDate || !isInRange(sessionDate)) return;

              const introducingParty = session.introducing_party ||
                session.host_name ||
                clientUser?.username ||
                clientUser?.name ||
                clientAdmin.primary_name ||
                clientAdmin.client_name ||
                'Unknown';

              let roleText = session.introducing_party_role || session.role || session.user_role || '';
              if (!roleText) roleText = extractRoleFromIntroducingParty(introducingParty);

              const sessionName = session.session_name || session.name || session.session_title || caseName;

              const exhibitsIntroduced = session.exhibits_introduced ?? session.exhibitsIntroduced ?? 0;
              const exhibitsReceived = session.exhibits_received ?? session.exhibitsReceived ?? 0;
              const videoEnabled = session.video_conference_enabled ?? session.video_enabled ?? false;

              card.rows.push({
                date: sessionDate,
                introducingParty,
                roleText,
                sessionTypeLabel: session._sessionType || 'Session',
                caseSession: sessionName,
                exhibitsIntroduced,
                exhibitsReceived,
                videoStatus: videoEnabled ? 'Enabled' : 'Not Enabled',
              });
            });
          })
        );

        const sortedCards = Object.values(cardsByClientAdminId)
          .map(card => ({
            ...card,
            rows: card.rows.sort((a, b) => a.date.getTime() - b.date.getTime())
          }))
          .filter(card => card.rows.length > 0);

        setUsageCards(sortedCards);
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
        toast.error('Failed to load usage report data.');
        setUsageCards([]);
      } finally {
        setLoadingUsage(false);
      }
    };

    fetchUsageData();
  }, [appliedStartDate, appliedEndDate, selectedResellerIds.join(','), role, userInfo.id]);

  const actionButtonStyle = {
    background: theme.gradients?.secondary || theme.colors.secondary,
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 'bold',
    fontSize: '12px',
    cursor: 'pointer',
    minWidth: '100px',
    height: '32px',
    fontFamily,
  };

  const dateInputStyle = {
    padding: '6px 10px',
    borderRadius: '999px',
    border: '1px solid #d0d7de',
    fontSize: '12px',
    height: '32px',
    minWidth: '150px',
    outline: 'none',
    fontFamily,
  };

  const handlePrint = () => window.print();

  const handleExport = () => {
    if (!usageCards.length || usageCards.every(c => !c.rows.length)) {
      toast.info('No data to export for this date range.');
      return;
    }

    const header = [
      'Reseller', 'Client Admin', 'Location', 'Date', 'Introducing Party', 'Role',
      'Session Type', 'Case / Session', 'Exhibits Introduced', 'Exhibits Received', 'Video Conference'
    ];

    const rows = [];
    usageCards.forEach(card => {
      const resellerName = card.reseller?.reseller_name || card.reseller?.username || 'Unknown';
      const clientName = card.clientAdmin?.client_name || card.clientAdmin?.primary_name || 'Unknown';

      card.rows.forEach(r => {
        rows.push([
          resellerName,
          clientName,
          card.location || '',
          formatDate(r.date),
          r.introducingParty || '',
          roleLabel(r.roleText || ''),
          displayText(r.sessionTypeLabel || ''),
          r.caseSession || '',
          r.exhibitsIntroduced ?? 0,
          r.exhibitsReceived ?? 0,
          r.videoStatus || ''
        ]);
      });
    });

    const csv = [header.map(value => csvHeading(displayText(value))), ...rows].map(row =>
      row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `usage-report-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleUpdateRange = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates.');
      return;
    }
    const s = parseDateSafely(startDate);
    const e = parseDateSafely(endDate);
    if (!s || !e || e.getTime() < s.getTime()) {
      toast.error('End date must be after start date.');
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  return (
    <div style={{
      fontFamily,
      backgroundColor: theme.colors.bgPrimary,
      minHeight: '100vh',
      paddingBottom: '3rem',
    }}>
      {/* TOP NAVIGATION TABS */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}>
        {tabs.map(tab => {
          const isActiveTab = isActive(tab);
          const label = tab === 'account' ? 'My Account' : tab === 'notify' ? 'Notify User' : tab.charAt(0).toUpperCase() + tab.slice(1);
          const iconClass =
            tab === 'resellers' ? 'briefcase-fill' :
            tab === 'clients' ? 'people-fill' :
            tab === 'reports' ? 'file-earmark-bar-graph-fill' :
            tab === 'notify' ? 'bell-fill' :
            tab === 'admins' ? 'buildings-fill' :
            'person-lines-fill';

          const isClientsTab = tab === 'clients' && role !== 'reseller';
          const isReportsTab = tab === 'reports';

          return (
            <div
              key={tab}
              style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
              ref={isClientsTab ? clientsDropdownRef : isReportsTab ? reportsDropdownRef : null}
              onMouseEnter={() => {
                if (isClientsTab) setIsClientsDropdownOpen(true);
                if (isReportsTab) setIsReportsDropdownOpen(true);
              }}
              onMouseLeave={() => {
                if (isClientsTab) setIsClientsDropdownOpen(false);
                if (isReportsTab) setIsReportsDropdownOpen(false);
              }}
            >
              <button
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  background: isActiveTab ? theme.gradients?.primary || theme.colors.primary : theme.gradients?.secondary || theme.colors.secondary,
                  color: '#fff',
                  cursor: isClientsTab || isReportsTab ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontFamily,
                }}
                onClick={() => !(isClientsTab || isReportsTab) && handleTabClick(tab)}
              >
                <i className={`bi bi-${iconClass}`} style={{ fontSize: '1rem' }}></i>
                {label}
              </button>

              {/* Clients Dropdown - only superadmin/admin */}
              {isClientsTab && isClientsDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '6px',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                  zIndex: 30,
                  width: '100%',
                  minWidth: '160px',
                  overflow: 'hidden',
                  fontFamily
                }}>
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily
                    }}
                    onClick={() => handleClientSubTab('core')}
                    onMouseOver={e => e.target.style.background = theme.gradients?.secondary || theme.colors.secondary}
                    onMouseOut={e => e.target.style.background = 'transparent'}
                  >{displayText(" Court Client ")}</button>
                  <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      textAlign: 'left',
                      background: 'transparent',
                      color: theme.colors.textPrimary,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontFamily
                    }}
                    onClick={() => handleClientSubTab('enterprise')}
                    onMouseOver={e => e.target.style.background = theme.gradients?.secondary || theme.colors.secondary}
                    onMouseOut={e => e.target.style.background = 'transparent'}
                  >{displayText(" Enterprise Client ")}</button>
                </div>
              )}

              {/* Reports Dropdown */}
              {isReportsTab && isReportsDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '6px',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                  zIndex: 30,
                  width: '100%',
                  minWidth: '180px',
                  overflow: 'hidden',
                  fontFamily
                }}>
                  {[
                    { key: 'user', label: 'User Report' },
                    { key: 'commission', label: 'Commission Report' },
                    { key: 'usage', label: 'Usage Report' }
                  ].map((item, idx, arr) => (
                    <React.Fragment key={item.key}>
                      <button
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          background: item.key === 'usage' ? (theme.gradients?.primary || theme.colors.primary) : 'transparent',
                          color: item.key === 'usage' ? '#fff' : theme.colors.textPrimary,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontFamily
                        }}
                        onClick={() => handleReportSubTab(item.key)}
                        onMouseOver={e => item.key !== 'usage' && (e.target.style.background = theme.gradients?.secondary || theme.colors.secondary)}
                        onMouseOut={e => item.key !== 'usage' && (e.target.style.background = 'transparent')}
                      >
                        {item.label}
                      </button>
                      {idx < arr.length - 1 && <hr style={{ margin: 0, borderTop: `1px solid ${theme.colors.borderColor}` }} />}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BREADCRUMB + ACTIONS */}
      <div style={{
        padding: '0 0.75rem',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <span
            style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 500, color: theme.colors.primary }}
            onClick={() => navigate(`${basePath}/reports?sub=usage`)}
          >
            Usage Report
          </span>
          <span style={{ margin: '0 4px' }}>/</span>
          <span style={{ color: theme.colors.textPrimary }}>Report</span>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" onClick={handlePrint} style={actionButtonStyle}>Print</button>
          <button type="button" onClick={handleExport} style={actionButtonStyle}>Export</button>
        </div>
      </div>

      {/* DATE RANGE CONTROLS */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0 0.75rem',
        marginBottom: '1.25rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontWeight: 500, fontSize: '12px' }}>Start Date:</span>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={dateInputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontWeight: 500, fontSize: '12px' }}>End Date:</span>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            style={dateInputStyle}
          />
        </div>
        <button onClick={handleUpdateRange} style={{ ...actionButtonStyle, background: theme.gradients?.primary || theme.colors.primary }}>
          Update
        </button>
      </div>

      {/* VIEW MODE DROPDOWN */}
<div style={{
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '0 0.75rem',
  marginBottom: '1rem'
}}>
  <select
    value={viewMode}
    onChange={(e) => {
      const value = e.target.value;
      setViewMode(value);
      // NO NAVIGATION - stays on same page!
    }}
    style={{
      padding: "6px 10px",
      borderRadius: "8px",
      border: "1px solid #ccc",
      fontSize: "13px",
      background: "#fff"
    }}
  >
    <option value="report">Report View</option>
    <option value="pie-chart">Pie Chart</option>
  </select>
</div>

      {/* MAIN CONTENT */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '1rem',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        margin: '0 0.75rem 20px',
        fontFamily,
      }}>
        {viewMode === "pie-chart" ? (
          <ReportPieChart
            reportType="usage"
            rawData={usageCards}
            isLoading={loadingUsage}
            defaultViewMode="pie-chart"
          />
        ) : (
          <>
            {loadingUsage ? (
              <div style={{ padding: '4rem 0', textAlign: 'center', color: '#666' }}>
                Loading usage data...
              </div>
            ) : !usageCards.length ? (
              <div style={{ padding: '4rem 0', textAlign: 'center', color: '#999' }}>
                No usage data found for the selected date range.
              </div>
            ) : (
              usageCards.map((card, idx) => {
                const reseller = card.reseller || {};
                const clientAdmin = card.clientAdmin || {};

                const resellerName =
                  reseller.reseller_name ||
                  reseller.name ||
                  reseller.username ||
                  'Unknown Reseller';

                const resellerSince =
                  reseller.reseller_since || reseller.created_at || '';

                const contactName =
                  reseller.primary_contact || reseller.contact_name || '';

                const email =
                  reseller.primary_email || reseller.email || '';

                const phone =
                  reseller.primary_phone || reseller.phone || '';

                const status =
                  (reseller.status || 'active').toLowerCase() === 'active'
                    ? 'Active'
                    : 'Inactive';

                return (
                  <div key={card.key || idx} style={{ marginBottom: '2rem' }}>
                    {/* Reseller Header */}
                    <div style={{
                      background: theme.gradients?.primary || theme.colors.primary,
                      color: 'white',
                      padding: '1rem 1.5rem',
                      borderRadius: '12px 12px 0 0',
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      gap: '1rem'
                    }}>
                      <div>
                        <div style={{ fontSize: '1.1em', fontWeight: 700 }}>{displayText("Reseller: ")}{resellerName}</div>
                        <div style={{ marginTop: '6px' }}>Since: {formatDate(resellerSince) || '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>Status: <span style={{ color: status === 'Active' ? '#d4edda' : '#f8d7da' }}>{status}</span></div>
                        <div style={{ marginTop: '6px' }}>Contact: {contactName || '—'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div>Email: {email || '—'}</div>
                        <div style={{ marginTop: '6px' }}>Phone: {phone || '—'}</div>
                      </div>
                    </div>

                    {/* Client Admin + Sessions */}
                    <div style={{
                      border: `1px solid ${theme.colors.borderColor}`,
                      borderTop: 'none',
                      borderRadius: '0 0 12px 12px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        background: '#00a0df',
                        color: 'white',
                        padding: '0.6rem 1rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span>{clientAdmin.client_name || clientAdmin.primary_name || displayText('Client Admin')}</span>
                        <span>Location: {card.location || '—'}</span>
                      </div>

                      {card.rows.length === 0 ? (
                        <div style={{ padding: '1.5rem', textAlign: 'center', color: '#666' }}>{displayText(" No sessions in this period ")}</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f1f3f5' }}>
                              <th style={{ padding: '0.7rem', textAlign: 'left' }}>Date</th>
                              <th style={{ padding: '0.7rem', textAlign: 'left' }}>User / Role</th>
                              <th style={{ padding: '0.7rem', textAlign: 'left' }}>{displayText("Session Type")}</th>
                              <th style={{ padding: '0.7rem', textAlign: 'left' }}>{displayText("Case / Session")}</th>
                              <th style={{ padding: '0.7rem', textAlign: 'center' }}>Exhibits In</th>
                              <th style={{ padding: '0.7rem', textAlign: 'center' }}>Exhibits Out</th>
                              <th style={{ padding: '0.7rem', textAlign: 'center' }}>Video</th>
                            </tr>
                          </thead>
                          <tbody>
                            {card.rows.map((row, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.7rem' }}>{formatDate(row.date)}</td>
                                <td style={{ padding: '0.7rem' }}>
                                  Exhibit{row.roleText ? `, ${roleLabel(row.roleText)}` : ''}
                                </td>
                                <td style={{ padding: '0.7rem' }}>{displayText(row.sessionTypeLabel)}</td>
                                <td style={{ padding: '0.7rem' }}>{row.caseSession}</td>
                                <td style={{ padding: '0.7rem', textAlign: 'center' }}>{row.exhibitsIntroduced}</td>
                                <td style={{ padding: '0.7rem', textAlign: 'center' }}>{row.exhibitsReceived}</td>
                                <td style={{ padding: '0.7rem', textAlign: 'center' }}>
                                  {row.videoStatus}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsageReports;